"use client"

import { ComponentConfig } from "@measured/puck"
import { Input, Spin } from "antd"
import { useEffect, useState } from "react"
import "swiper/css"
import "swiper/css/effect-fade"
import "swiper/css/pagination"
import { Autoplay, Navigation, Pagination } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import { TourItem, Tours } from "../../../../../../types/types"
import { generateImagePath } from "../../../../../../utils/generateImagePath"
import { fetchTours } from "../../../../../tour/services/tours"
import { DefaultImage, IconSearch } from "../../../../external-components"
import "./customSwiper.css"

export type PostGridByCategoryProps = {
  categoryId?: number
  isAutoPlay?: boolean
  isLoopImage?: boolean
  colorPrimaryTag?: string
  itemsToRender?: number | "All"
  showSearch?: boolean
  showTitle?: boolean
}

export const RenderGridTours: ComponentConfig<PostGridByCategoryProps> = {
  render: ({ categoryId, isAutoPlay, isLoopImage, colorPrimaryTag }) => {
    const [toursData, setToursData] = useState<Tours[]>([])
    const [loading, setLoading] = useState<boolean>(false)
    const [inputValue, setInputValue] = useState<string>("")

    const transformApiDataTours = (data: TourItem[]) => {
      const transformData =
        data &&
        data.map(item => {
          const { attributes } = item
          return {
            feature_img: attributes.feature_img,
            is_new: attributes.is_new,
            category: attributes.category.data.attributes.name,
            tags: attributes.tags.data.map(tag => tag.attributes.name),
            title: attributes.title,
            short_description: attributes.short_description,
            price_overview: attributes.price_overview,
            slug: attributes.slug,
          }
        })

      setToursData(transformData)
    }

    const fetchToursList = async (searchValue?: string) => {
      setLoading(true)

      const filter = {
        searchValue: searchValue,
        category: categoryId,
      }

      try {
        const response = await fetchTours(filter)
        transformApiDataTours(response.data ?? [])
      } catch (error) {
        console.log(error)
      } finally {
        setLoading(false)
      }
    }

    useEffect(() => {
      fetchToursList()
    }, [categoryId])

    const labelPriceOverview = (text: string) => {
      return (
        <>
          {text.split("/")[0]}
          <span className="text-[#4F4F4F]"> / {text.split("/")[1]}</span>
        </>
      )
    }

    const paginationCustom = {
      clickable: true,
      bulletClass: "custom-swiper-pagination-bullet",
      bulletActiveClass: "custom-swiper-pagination-bullet-active",
      renderBullet: function (index: number, className: string) {
        return `<span class="${className}">${""}</span>`
      },
    }

    const handleSearchTour = () => {
      if (inputValue === "") {
        fetchToursList()
      } else {
        fetchToursList(inputValue)
      }
    }

    const setColorPrimaryTag = colorPrimaryTag ? `bg-[${colorPrimaryTag}]` : "bg-[#0B9444]"

    return (
      <>
        <div>
          <Input
            styles={{
              affixWrapper: {
                padding: "0px",
                height: 48,
                borderRadius: 12,
                borderColor: "#D1D3D4",
                overflow: "hidden",
              },
              input: { padding: "12px 16px 12px 8px", color: "#8B8B90" },
              suffix: { padding: "0px" },
              prefix: { paddingLeft: "16px" },
            }}
            suffix={
              <div
                className="bg-[#0B9444] h-full flex text-center items-center px-4 py-3 text-white text-[0.9375rem] leading-6 cursor-pointer"
                onClick={handleSearchTour}
              >
                Search
              </div>
            }
            className="w-full sm:w-[37.5rem] m-auto flex"
            placeholder="Tours"
            prefix={<img src={IconSearch} />}
            onChange={e => setInputValue(e.target.value)}
            onPressEnter={handleSearchTour}
          />

          <div className="pt-10 pb-6 text-[#242F3E] text-2xl leading-7 font-semibold">
            {toursData.length ?? 0} Trips
          </div>

          {loading ? (
            <div className=" flex items-center justify-center  h-[70vh] w-full ">
              <Spin />
            </div>
          ) : (
            <>
              {toursData && toursData.length > 0 ? (
                <div className="flex flex-col gap-10 sm:grid sm:grid-cols-3">
                  {toursData &&
                    toursData.map((item, index) => (
                      <div className="flex flex-col gap-4" key={index}>
                        <div className="w-full rounded-2xl relative overflow-hidden">
                          <Swiper
                            slidesPerView={1}
                            pagination={toursData.length > 1 && paginationCustom}
                            modules={[Pagination, Autoplay, Navigation]}
                            navigation={{
                              nextEl: ".custom-next-slick-arrow-orange",
                              prevEl: ".custom-prev-slick-arrow-orange",
                            }}
                            loop={isLoopImage}
                            speed={1000}
                            autoplay={isAutoPlay}
                          >
                            {item.feature_img.map((img, index) => (
                              <SwiperSlide key={index}>
                                <a href={item.slug} rel="noopener noreferrer" className="block overflow-hidden group">
                                  <div className="transition-transform duration-300 ease-in-out transform relative">
                                    <div className={`overflow-hidden ${item.is_new ? "rounded-tl-[5.225rem]" : ""}`}>
                                      <img
                                        src={img ? generateImagePath(img, "small") : DefaultImage}
                                        alt={`Slide ${index}`}
                                        className="object-cover h-[16.875rem] w-full z-10 relative group-hover:scale-105 transition-all duration-300 rounded-2xl"
                                      />
                                    </div>

                                    {item.is_new && (
                                      <div className="absolute top-0 left-0 bg-gradient-to-tr from-[#FF2114] to-[#EB1E12] w-24 h-24">
                                        <p className="rotate-[-45deg] left-0.5 top-2 absolute text-sm leading-4 font-semibold text-white">
                                          New
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                </a>
                              </SwiperSlide>
                            ))}
                          </Swiper>
                        </div>

                        <div className="flex flex-row gap-2">
                          <div
                            className={`px-2 py-1.5 rounded-md uppercase flex flex-row gap-1 items-center text-xs leading-4 text-white font-semibold text-center`}
                            style={{ backgroundColor: `${setColorPrimaryTag}` }}
                          >
                            <img className="w-4 h-4 fill-white" src={IconSearch} />
                            <p>{item.category}</p>
                          </div>

                          {item.tags.map(tag => (
                            <div
                              className="px-2 py-1.5 rounded-md uppercase items-center text-xs leading-4 text-[#4F4F4F] bg-[#EBEDF0] font-semibold"
                              key={index}
                            >
                              {tag}
                            </div>
                          ))}
                        </div>

                        <div className="flex flex-col gap-2">
                          <h3 className="text-2xl leading-7 font-semibold text-[#242F3E]">{item.title}</h3>

                          <p className="text-[#4F4F4F] text-base leading-5.5 font-normal">{item.short_description}</p>
                        </div>

                        <p className="text-[#242F3E] text-base leading-[1.1875rem]">
                          From {item.price_overview ? labelPriceOverview(item.price_overview) : "Contact us for price"}
                        </p>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="flex items-center justify-center h-[30vh] w-full">
                  <p>No tour found, contact us for mor information</p>
                </div>
              )}
            </>
          )}
        </div>
      </>
    )
  },
}
